<template>
  <Modal
    title="Reference Id"
    class-name="small-modal"
    :saving="saving"
    @submit="submit"
    @hide-modal="emit('hide-modal')"
  >
    <div class="section-spacing-20">
      <TextField
        v-model="formData.referenceId"
        class="full-width"
        name="Reference Id"
        label="*Reference Id:"
        :error-message="firstErrorMessage(v$.referenceId)"
        data-cy="referenceIdInput"
        :success="validKey"
      />
      <TextArea
        v-model="formData.reason"
        name="Reason"
        label="*Reason:"
        :error-message="firstErrorMessage(v$.reason)"
        data-cy="reasonInput"
      />
      <div>
        <div class="d-flex align-items-start">
          <Checkbox
            v-model="formData.acknowledged"
            :class="['mr-2']"
            data-cy="acknowledgedCheckbox"
            :error="Boolean(firstErrorMessage(v$.acknowledged))"
          />
          <div>
            <div>
              I Acknowledge that the Reference Id is used to sync records
              between Sera and your accounting platform. Changing this key can
              cause the mapping to break, if not updated properly.
              <a
                href="https://help.sera.tech/hc/en-us/articles/25316880867095-Reference-ID"
                target="_blank"
                >Need help?</a
              >
            </div>
            <div class="error-text">
              {{ firstErrorMessage(v$.acknowledged) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Checkbox from "@/components/Checkbox.vue";
import Modal from "@/components/Modal.vue";
import TextArea from "@/components/TextArea.vue";
import TextField from "@/components/TextField.vue";
import { Transaction } from "@/typings/transaction";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, sameAs } from "@vuelidate/validators";

const store = useStore();

const emit = defineEmits(["hide-modal", "save-success"]);
const props = defineProps<{ transaction: Transaction }>();
const _transaction = computed(() => props.transaction);
const formData = ref({
  referenceId: _transaction.value.reference_id,
  reason: "",
  acknowledged: false
});
const saving = ref(false);
const validKey = ref(false);
const v$ = useVuelidate(
  {
    referenceId: { required, $autoDirty: true },
    reason: { required, $autoDirty: true },
    acknowledged: {
      required,
      $autoDirty: true,
      truthy: helpers.withMessage("This is a required field", sameAs(true))
    }
  },
  formData.value
);

async function submit() {
  const valid = await v$.value.$validate();
  if (!valid) return;
  saving.value = true;
  const { referenceId, reason } = formData.value;
  try {
    const { id, invoice_id } = props.transaction;

    await store.dispatch("setReferenceId", {
      id,
      invoice_id,
      data: {
        reference_id: referenceId,
        reference_id_change_reason: reason
      }
    });
    window.notify.success("Reference Id updated successfully!");
    emit("save-success");
    emit("hide-modal");
  } catch (e) {
    console.error(e);
  }
  saving.value = false;
}
</script>

<style scoped lang="scss"></style>
